import { useEffect, useState } from "react";
import { BiShow, BiUserCheck } from "react-icons/bi";
import { createColumnHelper } from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, IconButton, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";

import { useApiClient, usePaginationApi } from "../../shared/hooks";
import usePagination from "../../features/admin/usePagination";
import { Header } from "../../shared/components";
import { Table } from "../../features/admin/components";
import { ConfirmEnableModal, UserModal } from "../../features/admin/users/components";

function ViewDisabledUsersPage() {
  const queryClient = useQueryClient();
  const { getWithPagination } = usePaginationApi();
  const apiClient = useApiClient();
  const [modalData, setModalData] = useState<any | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEnableOpen,
    onOpen: onEnableOpen,
    onClose: onEnableClose,
  } = useDisclosure();
  const [pagination, setPagination] = usePagination();
  const columnHelper = createColumnHelper<any>();
  const toast = useToast();
  const [enableModalData, setEnableModalData] = useState<any | null>(null);
  const [isLoadingEnable, setIsLoadingEnable] = useState(false);

  const { isLoading, data } = useQuery({
    queryKey: ["disabled-user", pagination],
    queryFn: async () => {
      const response: any = await getWithPagination("PROFILE_SERVICE", "/api/profile/admin/list-disabled-users", {
        perpage: pagination.perPage,
        page: pagination.currentPage,
        search: pagination.searchTerm,
      });

      if (!response?.status) {
        throw new Error(response?.message);
      }

      return {
        data: response?.data.data,
      };
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data) {
      setPagination({
        ...pagination,
        currentPage: data?.data?.current_page ?? 1,
        perPage: pagination.perPage ?? 20,
        total: data?.data?.total_items ?? 0,
        totalPages: data?.data?.total_pages ?? 0,
      });
    }
  }, [data]);

  const onOpenModal = (rowData: any) => {
    setModalData(rowData);
    onOpen();
  };

  const onOpenEnableModal = (rowData: any) => {
    setEnableModalData(rowData);
    onEnableOpen();
  };

  const handleEnableUser = async () => {
    if (!enableModalData?.id) return;
    setIsLoadingEnable(true);

    try {
      await apiClient.enableDisableUser(enableModalData.email, "enabled")

      toast({
        title: "User Enabled",
        description: "User has been enabled successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      queryClient.invalidateQueries(["disabled-user", pagination]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to enable user",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsLoadingEnable(false);
      onEnableClose();
    }
  };

  const columns = [
    columnHelper.accessor("SN", {
      header: () => <span>SN</span>,
      size: 50,
      cell: (info: any) => {
        return (
          <span>
            {info.row.index + (pagination?.currentPage - 1) * pagination?.perPage + 1}
          </span>
        );
      },
    }),

    columnHelper.accessor("full_name", {
      id: "full_name",
      header: () => <span>Name</span>,
      size: 200,
      cell: (info: any) => {
        const full_name = info?.row?.original?.full_name || "";
        const capitalized = full_name.charAt(0).toUpperCase() + full_name.slice(1);
        return <span>{capitalized}</span>;
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span>Email</span>,
      size: 200,
      cell: (info: any) => {
        return <span>{info?.row?.original?.email}</span>;
      },
    }),

    columnHelper.accessor("action", {
      id: "action",
      header: () => <span>{"Action"}</span>,
      size: 200,
      cell: (info: any) => {
        return (
          <span>
            <Tooltip label="View User" fontSize="md">
              <IconButton
                aria-label="View"
                icon={<BiShow size={"24px"} />}
                onClick={() => onOpenModal(info.row.original)}
              />
            </Tooltip>
            <Tooltip label="Enable User" fontSize="md">
              <IconButton
                aria-label="Enable"
                icon={<BiUserCheck size={"24px"} />}
                onClick={() => onOpenEnableModal(info.row.original)}
                ml={2}
              />
            </Tooltip>
          </span>
        );
      },
    }),
  ];

  return (
    <Box>
      <Header
        buttonIcon={null}
        header="List Of User"
        buttonText={undefined}
        handleOpen={onOpen}
      />
      <Table
        title="List of Disabled Users"
        columns={columns}
        rowData={data?.data?.data_list ?? []}
        pagination={pagination}
        setPagination={setPagination}
        loading={isLoading}
      />
      <UserModal
        isOpen={isOpen}
        {...modalData}
        onClose={onClose}
      />
      <ConfirmEnableModal
        isOpen={isEnableOpen}
        fullName={enableModalData?.full_name}
        email={enableModalData?.email}
        onClose={onEnableClose}
        onConfirm={handleEnableUser}
        isLoading={isLoadingEnable}
      />
    </Box>
  );
}

export default ViewDisabledUsersPage;
